import { useId } from 'react';
import type { ReactElement } from 'react';

/**
 * The Parol set, ported from mockup-christmas-options.html (B Parol).
 * Parol and string colours are fixed warm lantern colours on every theme, as
 * in the mock-up; the gold ink (belen, tree, mistletoe) follows the theme
 * through --deco-gold / --deco-baby.
 */

function starPoints(cx: number, cy: number, outer: number, inner: number): string {
  const points: string[] = [];
  for (let i = 0; i < 10; i += 1) {
    const angle = ((-90 + (i * 180) / 5) * Math.PI) / 180;
    const radius = i % 2 === 1 ? inner : outer;
    points.push(
      `${(cx + radius * Math.cos(angle)).toFixed(1)},${(cy + radius * Math.sin(angle)).toFixed(1)}`,
    );
  }
  return points.join(' ');
}

const TAILS = ['#e8503a', '#f2c14e', '#3f9a62'];

/** A Filipino star lantern. cx, cy and R are in the parent SVG's units. */
export function Parol({
  cx,
  cy,
  r,
  colors,
}: {
  cx: number;
  cy: number;
  r: number;
  colors: [string, string];
}): ReactElement {
  const gradientId = `parol-${useId().replace(/:/g, '')}`;
  const tailLength = r * 0.55;
  const tailY = cy + r * 0.78;
  const tailWidth = Math.max(1, r * 0.06);
  return (
    <g>
      <defs>
        <radialGradient id={gradientId} cx="50%" cy="45%" r="60%">
          <stop offset="0" stopColor="#fff7d6" />
          <stop offset=".45" stopColor={colors[0]} />
          <stop offset="1" stopColor={colors[1]} />
        </radialGradient>
      </defs>
      {[0, 1, 2].map((t) => {
        const offset = (t - 1) * r * 0.09;
        return (
          <g key={t} fill="none" strokeWidth={tailWidth} strokeLinecap="round">
            <path
              d={`M${cx - tailLength * 0.6 + offset} ${tailY} q ${-r * 0.12} ${r * 0.5} ${r * 0.04} ${r * 1.1}`}
              stroke={TAILS[t]}
            />
            <path
              d={`M${cx + tailLength * 0.6 + offset} ${tailY} q ${r * 0.12} ${r * 0.5} ${-r * 0.04} ${r * 1.1}`}
              stroke={TAILS[2 - t]}
            />
          </g>
        );
      })}
      <circle
        cx={cx}
        cy={cy}
        r={r * 1.08}
        fill="none"
        stroke="#f6d27a"
        strokeWidth={Math.max(0.8, r * 0.04)}
        opacity=".55"
      />
      <polygon
        points={starPoints(cx, cy, r, r * 0.43)}
        fill={`url(#${gradientId})`}
        stroke="#f8dc8c"
        strokeWidth={Math.max(0.8, r * 0.045)}
        strokeLinejoin="round"
      />
      <polygon points={starPoints(cx, cy, r * 0.55, r * 0.24)} fill="#fff4cf" opacity=".75" />
      <circle cx={cx} cy={cy} r={r * 0.13} fill="#fff" />
    </g>
  );
}

/** The nativity house: roof, two figures, a manger. Ink follows the theme. */
export function Belen({ x, y, scale }: { x: number; y: number; scale: number }): ReactElement {
  const transform = `translate(${x} ${y}) scale(${scale})`;
  return (
    <g>
      <g transform={transform} style={{ fill: 'var(--deco-gold)', stroke: 'var(--deco-gold)' }}>
        <path
          d="M65 3 L67.2 9 L73.5 9.2 L68.5 13 L70.3 19 L65 15.4 L59.7 19 L61.5 13 L56.5 9.2 L62.8 9 Z"
          stroke="none"
        />
        <polyline points="8,54 65,26 122,54" fill="none" strokeWidth="3" strokeLinejoin="round" />
        <line x1="17" y1="50" x2="17" y2="104" strokeWidth="3" />
        <line x1="113" y1="50" x2="113" y2="104" strokeWidth="3" />
        <line x1="2" y1="104.5" x2="128" y2="104.5" strokeWidth="2" />
        <circle cx="40" cy="73" r="5" stroke="none" />
        <path d="M33 81 Q40 75 47 81 L52 104 L29 104 Z" stroke="none" />
        <circle cx="90" cy="62" r="5" stroke="none" />
        <path d="M84 70 Q90 66 96 70 L99 104 L81 104 Z" stroke="none" />
        <line x1="101" y1="57" x2="103" y2="104" strokeWidth="1.8" />
        <path d="M55 96 L75 96 L71 104 L59 104 Z" stroke="none" />
      </g>
      <g transform={transform} style={{ fill: 'var(--deco-baby)' }}>
        <ellipse cx="66" cy="93" rx="7" ry="3.4" />
        <circle cx="59.5" cy="92" r="2.6" />
      </g>
    </g>
  );
}

const ORNAMENTS = ['#fff4cf', '#f08c5a', '#9ec1ff'];
const ORNAMENT_SPOTS: [number, number][] = [
  [-0.12, 0.4],
  [0.14, 0.5],
  [-0.2, 0.62],
  [0.08, 0.66],
  [0.22, 0.8],
  [-0.06, 0.78],
  [-0.25, 0.88],
  [0.02, 0.92],
];
const TIERS: [number, number, number][] = [
  [0, 0.38, 0.42],
  [0.2, 0.62, 0.72],
  [0.42, 0.9, 1],
];

/** A small tree in the theme's gold, with a few twinkling ornaments. */
export function GoldTree({ cx, base, h }: { cx: number; base: number; h: number }): ReactElement {
  const w = h * 0.62;
  return (
    <g>
      <rect
        x={cx - h * 0.045}
        y={base - h * 0.1}
        width={h * 0.09}
        height={h * 0.1}
        fill="#8a6a35"
      />
      {TIERS.map(([from, to, width]) => {
        const top = base - h + from * h;
        const bottom = base - h + to * h;
        const half = (w * width) / 2;
        return (
          <polygon
            key={from}
            points={`${cx},${top} ${cx - half},${bottom} ${cx + half},${bottom}`}
            style={{ fill: 'var(--deco-gold)' }}
          />
        );
      })}
      <polygon points={starPoints(cx, base - h - 2, h * 0.085, h * 0.036)} fill="#f1c856" />
      {ORNAMENT_SPOTS.map(([dx, dy], index) => (
        <circle
          key={index}
          className="xmas-twinkle"
          style={{ animationDelay: `${(index * 0.3).toFixed(1)}s` }}
          cx={cx + dx * h}
          cy={base - h + dy * h}
          r={h * 0.028}
          fill={ORNAMENTS[index % ORNAMENTS.length]}
        />
      ))}
    </g>
  );
}

/** A gold mistletoe sprig with a navy bow and pale berries, hung on a string. */
export function Mistletoe({ x, y, width }: { x: number; y: number; width: number }): ReactElement {
  return (
    <svg
      className="xmas-mistletoe"
      x={x}
      y={y}
      width={width}
      height={(80 * width) / 60}
      viewBox="0 0 60 80"
    >
      <line x1="30" y1="0" x2="30" y2="19" stroke="#b9a37a" strokeWidth="1" />
      <ellipse cx="20" cy="44" rx="5" ry="14" transform="rotate(-40 20 44)" fill="#c9a14a" />
      <ellipse cx="40" cy="44" rx="5" ry="14" transform="rotate(40 40 44)" fill="#a9823a" />
      <ellipse cx="24" cy="56" rx="4.5" ry="13" transform="rotate(-15 24 56)" fill="#a9823a" />
      <ellipse cx="36" cy="56" rx="4.5" ry="13" transform="rotate(15 36 56)" fill="#c9a14a" />
      <path d="M30 22 C 22 13, 13 17, 18 24 C 22 28, 28 24, 30 22 Z" fill="#b8323a" />
      <path d="M30 22 C 38 13, 47 17, 42 24 C 38 28, 32 24, 30 22 Z" fill="#b8323a" />
      <path d="M29 23 L23 34 L26.5 33 Z M31 23 L37 34 L33.5 33 Z" fill="#8f242c" />
      <circle cx="30" cy="22.5" r="2.6" fill="#8f242c" />
      <circle cx="27" cy="40" r="3.3" fill="#f3e7c4" />
      <circle cx="33" cy="40" r="3.3" fill="#f3e7c4" />
      <circle cx="30" cy="45" r="3.3" fill="#f3e7c4" />
    </svg>
  );
}
